import { parseEntityRef, stringifyEntityRef, type Entity } from '@backstage/catalog-model';
import { Progress } from '@backstage/core-components';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { EntityRelationsGraph, type EntityNode } from '@backstage/plugin-catalog-graph';
import { useCallback, useEffect, useRef, type RefObject } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEntityRefLink } from '@backstage/plugin-catalog-react';
import { useAsync } from 'react-use';
import Tooltip from '@material-ui/core/Tooltip';

const NODE_WIDTH = 180;
const TITLE_LINE_HEIGHT = 15;
const TITLE_MAX_LINES = 2;
const INITIAL_NODE_SIZE = 72;
const TITLE_MAX_WIDTH = NODE_WIDTH - 28;
const MIN_READABLE_NODE_HEIGHT = 28;

/** Hide SVG labels when the graph has scaled the nodes below readable size. */
export function useCompactGraphLabels(containerRef: RefObject<HTMLDivElement>) {
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    let frame = 0;
    const update = () => {
      frame = 0;
      const rect = container.querySelector<SVGRectElement>('.rdap-catalog-node rect');
      if (!rect) return;
      container.dataset.rdapHideNodeLabels = String(
        rect.getBoundingClientRect().height < MIN_READABLE_NODE_HEIGHT,
      );
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    const mutations = new MutationObserver(schedule);
    mutations.observe(container, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['viewBox', 'transform'],
    });
    const resize = new ResizeObserver(schedule);
    resize.observe(container);
    schedule();

    return () => {
      mutations.disconnect();
      resize.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
  }, [containerRef]);
}

function estimatedTitleWidth(text: string): number {
  return [...text].reduce((width, character) => {
    if (/[MW@%]/.test(character)) return width + 13;
    if (/[ilI1.,' ]/.test(character)) return width + 5;
    if (/[A-Z0-9]/.test(character)) return width + 9;
    return width + 8;
  }, 0);
}

/** Keep every rendered line inside the node; use initials if the title cannot fit. */
function nodeLabel(text: string): { lines: string[]; initials?: string } {
  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    if (estimatedTitleWidth(word) > TITLE_MAX_WIDTH) break;
    const candidate = current ? `${current} ${word}` : word;
    if (current && estimatedTitleWidth(candidate) > TITLE_MAX_WIDTH) {
      lines.push(current);
      current = word;
    } else {
      current = candidate;
    }
  }
  if (current) lines.push(current);
  if (lines.length <= TITLE_MAX_LINES && lines.join(' ').length === words.join(' ').length) {
    return { lines: lines.length ? lines : [text] };
  }
  const initials = words.length > 1
    ? words.slice(0, 3).map(word => word[0]).join('')
    : (words[0] ?? '?').slice(0, 2);
  return { lines: [], initials: initials.toLocaleUpperCase() };
}

/** Node styling used only by the dedicated full Catalog Graph page. */
export function RdapCatalogNode({
  node,
  selectedEntityRefs = [],
}: {
  node: EntityNode;
  selectedEntityRefs?: string[];
}) {
  const entity = node.entity;
  const title = entity.metadata.title ?? entity.metadata.name;
  const selected = selectedEntityRefs.includes(stringifyEntityRef(entity));
  const { lines, initials } = nodeLabel(title);
  const compact = Boolean(initials);
  const width = compact ? INITIAL_NODE_SIZE : NODE_WIDTH;
  const titleBlock = lines.length * TITLE_LINE_HEIGHT;
  const contentHeight = titleBlock + 8 + 10;
  const height = compact ? INITIAL_NODE_SIZE : Math.max(70, Math.round(contentHeight + 24));
  const contentTop = (height - contentHeight) / 2;
  const cx = width / 2;
  const kindClass = entity.kind.toLocaleLowerCase('en-US').replace(/[^a-z0-9-]/g, '-');

  const graphic = (
    <g
      className={`rdap-catalog-node rdap-catalog-node--${kindClass}${selected ? ' rdap-catalog-node--selected' : ''}`}
      onClick={node.onClick}
      style={{ cursor: node.onClick ? 'pointer' : 'default' }}
      aria-label={`${title}, ${entity.kind}`}
    >
      <rect width={width} height={height} rx={height / 2} strokeWidth="2" />
      {compact ? (
        <text className="rdap-catalog-node__initials" x={cx} y={height / 2} textAnchor="middle" dominantBaseline="central">{initials}</text>
      ) : (
        <>
          <text className="rdap-catalog-node__title" x={cx} textAnchor="middle">
            {lines.map((line, index) => (
              <tspan key={index} x={cx} y={contentTop + 13 + index * TITLE_LINE_HEIGHT}>{line}</tspan>
            ))}
          </text>
          <text className="rdap-catalog-node__kind" x={cx} y={contentTop + titleBlock + 8 + 10} textAnchor="middle">{entity.kind.toUpperCase()}</text>
        </>
      )}
    </g>
  );

  return (
    <Tooltip
      arrow
      placement="top"
      enterDelay={150}
      classes={{ tooltip: 'rdap-catalog-node-tooltip', arrow: 'rdap-catalog-node-tooltip__arrow' }}
      title={(
        <div>
          <strong>{title}</strong>
          <span>{entity.kind}</span>
          {entity.metadata.description && <p>{entity.metadata.description}</p>}
        </div>
      )}
    >
      {graphic}
    </Tooltip>
  );
}

const GRAPH_KINDS = [
  'Tenant',
  'Domain',
  'System',
  'Component',
  'API',
  'Resource',
  'Template',
];

const GRAPH_RELATIONS = [
  'hasPart',
  'partOf',
  'dependsOn',
  'dependencyOf',
  'providesApi',
  'apiProvidedBy',
  'consumesApi',
  'apiConsumedBy',
  'ownedBy',
  'ownerOf',
];
const isIncludedTarget = (relation: { type: string; targetRef: string }) => {
  if (!GRAPH_RELATIONS.includes(relation.type)) return false;
  try {
    const kind = parseEntityRef(relation.targetRef).kind.toLocaleLowerCase('en-US');
    return GRAPH_KINDS.some(item => item.toLocaleLowerCase('en-US') === kind);
  } catch {
    return false;
  }
};

/**
 * Entity-card preview using the same Catalog Graph component and query path as
 * the standard Backstage graph. The preview deliberately has no custom canvas
 * or hand-built graph data.
 */
export function CatalogGraphFocus({
  focusRef,
  navigateOnNodeClick = false,
}: {
  focusRef: string;
  navigateOnNodeClick?: boolean;
}) {
  const graphContainerRef = useRef<HTMLDivElement>(null);
  useCompactGraphLabels(graphContainerRef);
  const catalogApi = useApi(catalogApiRef);
  const configApi = useApi(configApiRef);
  const navigate = useNavigate();
  const entityHref = useEntityRefLink();
  const maxDepth = configApi
    .getOptionalConfig('rdap.knowledgeGraph')
    ?.getOptionalNumber('architectureMaxDepth') ?? 1;
  const { value: allowedRefs, loading } = useAsync(async () => {
    const allowed = new Set([focusRef]);
    let frontier = [focusRef];
    for (let depth = 0; depth < maxDepth && frontier.length > 0; depth += 1) {
      const { items } = await catalogApi.getEntitiesByRefs({ entityRefs: frontier });
      const next = new Set<string>();
      for (const entity of items.filter((item): item is Entity => Boolean(item))) {
        for (const relation of entity.relations ?? []) {
          if (!isIncludedTarget(relation)) continue;
          if (!allowed.has(relation.targetRef)) {
            allowed.add(relation.targetRef);
            next.add(relation.targetRef);
          }
        }
      }
      frontier = [...next];
    }
    return allowed;
  }, [catalogApi, focusRef, maxDepth]);
  const entityFilter = useCallback(
    (entity: Entity) => Boolean(allowedRefs?.has(stringifyEntityRef(entity))),
    [allowedRefs],
  );
  const onNodeClick = useCallback((node: EntityNode) => {
    navigate(entityHref(node.entity));
  }, [entityHref, navigate]);

  if (loading || !allowedRefs) return <Progress />;

  return (
    <div className="rdap-catalog-graph-focus" ref={graphContainerRef}>
      <EntityRelationsGraph
        rootEntityNames={parseEntityRef(focusRef)}
        maxDepth={maxDepth}
        unidirectional
        mergeRelations
        direction="TB"
        kinds={GRAPH_KINDS}
        relations={GRAPH_RELATIONS}
        entityFilter={entityFilter}
        onNodeClick={navigateOnNodeClick ? onNodeClick : undefined}
        zoom="enabled"
        showArrowHeads
        allowFullscreen
        renderNode={({ node }) => (
          <RdapCatalogNode
            node={node as EntityNode}
            selectedEntityRefs={[focusRef]}
          />
        )}
      />
    </div>
  );
}
